import { customEvents } from '@apia/util';
import { notify } from '@apia/notifications';
import {
  TFieldValidationResult,
  TSuccessfulValidation,
  TValidationResult,
  IFieldDefinition,
  TValidationField,
} from './types';
import { validateByRules } from './validationMethods';

import type formcheckLanguage from './lang/es';
import { validationsStore } from './validationStore';

declare global {
  interface Window {
    NO_LANGUAGE_EXCEPTION: string;
    MSG_INVALID_REG_EXP: string;
    GNR_INVALID_NAME: string;
  }
}

/**
 * Realiza la validación del campo tomando en cuenta las
 * distintas posibilidades de validación.
 */
export async function validateField(
  field: TValidationField<unknown>,
  formName: string,
): Promise<TFieldValidationResult> {
  let validationResult: TFieldValidationResult | null = null;
  const fieldValue = field.validationValueParser
    ? field.validationValueParser(field.value)
    : field.value;
  if (field.validationFunction) {
    validationResult = await field.validationFunction(fieldValue, formName);
  }
  if (
    (!field.validationFunction || validationResult === true) &&
    field.validationRules
  ) {
    validationResult = validateByRules({
      rules: field.validationRules,
      value: fieldValue,
    });
  } else if (validationResult === null) validationResult = true;
  return validationResult;
}

/**
 * Determina si la validación de un campo resultó exitosa.
 */
export function hasSucceedFieldValidation(
  result: TFieldValidationResult,
): result is true {
  return result === true;
}

/**
 * Determina si la validación de un formulario resultó
 * exitosa. Está pensada para utilizar en el callback
 * onValidate de Form o en la acción validationsActions.validateForm
 */
export function hasSucceedFormValidation<
  ValueType extends Record<string, unknown>,
  SubmitValueType extends Record<keyof ValueType, unknown> = ValueType,
>(
  result: TValidationResult<ValueType, SubmitValueType>,
): result is TSuccessfulValidation<ValueType, SubmitValueType> {
  return !('errors' in result);
}

/**
 * Intenta hacer foco en el campo y devuelve un booleano
 * indicando si se logró o no.
 */
export async function focusOnField(formName: string, field: TValidationField) {
  if (field.focus) {
    return (await field.focus()) || true;
  }

  const formElement = document.getElementById(
    `ValidationForm-${formName}`,
  ) as HTMLFormElement;
  const fieldElement: HTMLElement | null = formElement?.querySelector(
    `[name="${CSS.escape(field.name)}"], #${CSS.escape(field.name)}`,
  );

  if (fieldElement) {
    return new Promise((resolve) => {
      const event = new CustomEvent(customEvents.focus, { bubbles: true });
      fieldElement.focus();
      fieldElement.dispatchEvent(event);
      fieldElement.scrollIntoView({
        block: 'center',
        behavior: 'auto',
      });
      resolve(true);
    });
  }
  return false;
}

let langDefinition: typeof formcheckLanguage;
/**
 * Devuelve la cadena correspondiente a un código
 * de error, realizando los reemplazos correspondientes.
 */
export function lang(
  errorCode: keyof typeof formcheckLanguage,
  ...replacements: (string | number)[]
) {
  if (!langDefinition[errorCode]) return '';
  let returnString = langDefinition[errorCode];
  replacements.forEach((replace, index) => {
    returnString = returnString.replace(`%${index}`, replace.toString());
  });
  return returnString;
}

export function classToValidationFunction<T = string>(
  className: string,
): TValidationField<T>['validationFunction'] {
  // validate[confirm:fieldName:fieldTitle?]
  const match = className?.match(/'confirm:(\w+):(.+?)']/);
  if (match) {
    const validationFunction: TValidationField<T>['validationFunction'] = (
      value,
      formName,
    ) => {
      return value === validationsStore.getFieldStore(formName, match[1])?.value
        ? true
        : lang('confirm', match[2] ?? match[1]);
    };

    return validationFunction;
  }

  return undefined;
}

export function classToValidate(
  className: string,
): IFieldDefinition['validate'] {
  const validations: Record<string, unknown> = {};
  if (className) {
    const match = className.match(/validate\[([^\]]+)\]/);
    if (match) {
      const definitions = match[1].replaceAll('~', '').split(',');
      definitions.forEach((definition) => {
        const definitionMatch = definition.match(/(\w+)(?:=([\w\d]+))?/);
        if (definitionMatch) {
          const prop = definitionMatch[1] as keyof NonNullable<
            IFieldDefinition['validate']
          >;
          if (prop === 'pattern' && definitionMatch[2])
            validations[prop] = new RegExp(definitionMatch[2]);
          else if (definitionMatch[2]) {
            const value = definitionMatch[2].split(',');
            const actualValue = (
              value.length < 2 ? value.join() : value
            ) as NonNullable<IFieldDefinition['validate']>[typeof prop];
            validations[prop] =
              actualValue as (typeof validations)[typeof prop];
          } else validations[prop] = true;
        }
      });
      return validations as IFieldDefinition['validate'];
    }
  }
  return {};
}

export const getValidationErrorMessagesByLang = async (
  langCode = window.LANG_CODE,
): Promise<typeof formcheckLanguage> => {
  return (
    (await import(
      /* webpackChunkName: "val-lang-[request]" */ `./lang/${langCode}.ts`
    ).catch((error) => {
      throw error;
    })) as { default: typeof formcheckLanguage }
  ).default;
};

/**
 * Carga el archivo con mensajes de errores según el lenguaje
 * especificado.
 */
export const getErrorsMapByLang = async (
  langCode = window.LANG_CODE,
): Promise<typeof formcheckLanguage> => {
  return (
    (await import(
      /* webpackChunkName: "val-lang-[request]" */ `./lang/${langCode}.ts`
    ).catch((error) => {
      throw error;
    })) as { default: typeof formcheckLanguage }
  ).default;
};

/**
 * Realiza la carga de los mensajes de errores para el lenguaje
 * especificado. Este método debe llamarse antes de utilizar
 * las validaciones.
 */
export async function initValidations() {
  if (langDefinition) return;
  const code = document.documentElement.lang ?? 'es';
  langDefinition = (
    (await import(
      /* webpackChunkName: "val-lang-[request]" */ `./lang/${code}.ts`
    ).catch((error) => {
      notify({
        type: 'danger',
        message: window.NO_LANGUAGE_EXCEPTION,
      });
      throw error;
    })) as { default: typeof formcheckLanguage }
  ).default;
}

/**
 * Devuelve una función validadora, que devolverá true
 * o una cadena con el mensaje de error correspondiente
 * según el lenguaje actual y las reglas de validación
 * establecidas.
 */
export function getValidations(outerValidation: IFieldDefinition['validate']): {
  validate: (v: unknown) => string | true;
} {
  const validation = { ...outerValidation };
  if (!validation)
    return {
      validate: () => {
        return true;
      },
    } as { validate: () => true };

  return {
    validate: (outerV: unknown) => {
      const v = outerV ?? '';
      let returnValue: boolean | string = true;

      if (returnValue && validation.pattern) {
        let actualRegexp: RegExp;

        if (!(validation.pattern instanceof RegExp)) {
          const patternResult = validation.pattern.match(
            /(?:^\/(.+)\/(\w+)?$)|(^.*$)/,
          ) as RegExpMatchArray;

          const regexp = patternResult[1];
          const flags = patternResult[2];
          const stringPattern = patternResult[3];

          actualRegexp = new RegExp(
            regexp || stringPattern,
            flags || undefined,
          );
        } else actualRegexp = validation.pattern;

        returnValue =
          !!String(v).match(actualRegexp) ||
          (validation.patternMessage ?? window.MSG_INVALID_REG_EXP);
      }
      if (returnValue === true && validation.maxLength)
        returnValue =
          String(v).length <= validation.maxLength ||
          lang('lengthmax', validation.maxLength);
      if (returnValue === true && validation.minLength)
        returnValue =
          String(v).length >= validation.minLength ||
          lang('lengthmin', validation.minLength);
      if (returnValue === true && validation.max && validation.min)
        returnValue =
          (Number(v) <= validation.max && Number(v) >= validation.min) ||
          lang('digitltd', validation.min, validation.max);
      if (returnValue === true && validation.min)
        returnValue =
          Number(v) >= validation.min || lang('digitmin', validation.min);
      if (returnValue === true && validation.max)
        returnValue =
          Number(v) <= validation.max || lang('digitltd', 0, validation.max);
      if (returnValue === true && validation.validName)
        returnValue =
          !!String(v).match(/^[a-zA-Z0-9_.]*$/) || window.GNR_INVALID_NAME;
      if (returnValue === true && (validation.alpha || validation.nodigit))
        returnValue = !!String(v).match(/^[a-zA-Z]*$/) || lang('alpha');
      if (returnValue === true && validation.alphanum)
        returnValue = !!String(v).match(/^[a-zA-Z0-9]*$/) || lang('alphanum');
      if (returnValue === true && (validation.digit || validation.number))
        returnValue = !!String(v).match(/^[0-9]*$/) || lang('digit');
      if (returnValue === true && validation.email)
        returnValue =
          !!String(v).match(
            // eslint-disable-next-line no-control-regex
            /(?:^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i,
          ) || lang('email');
      if (returnValue === true && validation.differs)
        returnValue =
          v !== validation.differs || lang('differs', validation.differs);
      if (returnValue === true && validation.length_str)
        returnValue =
          (String(v).length >= validation.length_str[0] &&
            String(v).length <= validation.length_str[1]) ||
          lang('length_str', ...validation.length_str);
      if (returnValue === true && validation.length_fix)
        returnValue =
          String(v).length === validation.length_fix ||
          lang('length_fix', validation.length_fix);
      if (returnValue === true && validation.words_min) {
        const words = String(v).split(' ').length;
        return (
          words >= validation.words_min ||
          lang('words_min', validation.words_min)
        );
      }
      if (returnValue === true && validation.words_max) {
        const words = String(v).split(' ').length;
        return (
          words <= validation.words_max ||
          lang('words_max', validation.words_max)
        );
      }
      if (returnValue === true && validation.words_range) {
        const words = String(v).split(' ').length;
        return (
          (words <= validation.words_range[0] &&
            words >= validation.words_range[1]) ||
          lang('words_range', ...validation.words_range)
        );
      }
      if (returnValue === true && validation.required)
        returnValue =
          (Array.isArray(outerV) && outerV.length > 0) ||
          typeof outerV === 'number' ||
          (!Array.isArray(v) && !!v) ||
          lang('required');

      return returnValue;
    },
  };
}
